import { useRef } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { FileUpload } from '@amzn/stencil-react-components/file-upload';
import { IconCameraSmall } from '@amzn/stencil-react-icons';

interface AttachmentsInputProps {
  /** Currently attached files (controlled). */
  files: File[];
  /** Called with the full updated file list on any attach/remove. */
  onFilesChange: (files: File[]) => void;
  /** Label for the camera/video capture button. */
  takePhotoLabel: string;
  /** Text shown in the drag-and-drop zone. */
  dragAndDropText: string;
  /**
   * Accepted types for the file picker (extensions or MIME types).
   * Defaults to images only. The camera button accepts both image
   * and video capture.
   */
  accept?: string;
  /** Disables both the camera button and the file picker. */
  disabled?: boolean;
}

/**
 * Attachment picker used across mobile forms (task performance, audit
 * question responses): a capture button that opens the device camera
 * for photos or video (via a hidden `capture="environment"` file input
 * accepting `image/*,video/*`) plus the Stencil FileUpload dropzone for
 * picking existing files.
 *
 * Mirrors the GBSAVibeCodeTemplate pattern so all attachment entry points
 * look and behave identically. Purely presentational — owns no upload
 * logic; callers receive File objects and orchestrate uploads.
 */
export function AttachmentsInput({
  files,
  onFilesChange,
  takePhotoLabel,
  dragAndDropText,
  accept = 'image/*',
  disabled = false,
}: AttachmentsInputProps): JSX.Element {
  const cameraInputRef = useRef<HTMLInputElement>(null);

  return (
    <Col gridGap="dimensions.spacing.200">
      <Button
        variant={ButtonVariant.Secondary}
        icon={<IconCameraSmall />}
        disabled={disabled}
        onClick={() => cameraInputRef.current?.click()}
      >
        {takePhotoLabel}
      </Button>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        hidden
        aria-hidden
        data-testid="attachments-camera-input"
        onChange={(e) => {
          if (e.target.files?.length) {
            onFilesChange([...files, ...Array.from(e.target.files)]);
          }
          // Reset so re-taking the same photo/video fires onChange again.
          e.target.value = '';
        }}
      />
      <FileUpload
        accept={accept}
        files={files}
        isMulti
        isAppendable
        disabled={disabled}
        dragAndDropText={dragAndDropText}
        onFileAttached={(updatedFiles) => onFilesChange(updatedFiles)}
      />
    </Col>
  );
}
